import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { GraduationCap, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { setPortalSession, getPortalToken } from './portalStorage';
import { bootstrapPortalPwa } from './portalPwa';
import PortalInstallPrompt from './PortalInstallPrompt';

export default function PortalLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    bootstrapPortalPwa();
    if (getPortalToken()) {
      navigate('/portal/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/portal/login', { email, password });
      setPortalSession(response.data.access_token, response.data.user);
      toast.success('Willkommen im Eltern-Portal!');
      navigate('/portal/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login fehlgeschlagen';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-red/5 via-blue-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-red/10 to-blue-600 text-white mb-4">
            <GraduationCap className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Eltern-Portal</h1>
          <p className="text-slate-600 mt-1">Musikschule Online-Service · auch als App installierbar</p>
        </div>
        <PortalInstallPrompt />

        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">Anmelden</CardTitle>
            <CardDescription>
              Melden Sie sich mit Ihrer E-Mail-Adresse an
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ihre.email@beispiel.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="portal-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="portal-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-brand-red/10 to-blue-600 hover:from-brand-red/10 hover:to-blue-700"
                disabled={loading}
                data-testid="portal-login-btn"
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                className="text-sm text-brand-red hover:text-brand-red"
                onClick={() => toast.info('Bitte wenden Sie sich an das Sekretariat, um Ihr Passwort zurückzusetzen.')}
                data-testid="portal-forgot-password"
              >
                Passwort vergessen?
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Noch keinen Zugang? Kontaktieren Sie das Sekretariat.
        </p>

        {/* Back to main site */}
        <div className="text-center mt-4">
          <a 
            href="/login" 
            className="text-sm text-slate-400 hover:text-slate-600"
          >
            ← Zurück zur Verwaltung
          </a>
        </div>
      </div>
    </div>
  );
}
